import {
  DocumentPickerResponse,
  File,
  Fileish,
  firebase,
  firestore,
  IUserContext,
  logger,
  PermissionCameraWrapper,
  RowItem,
  useAlert,
  userContext,
  useTheme,
  VectorIcon,
  VectorIconProps,
} from "capsule"
import React, { ChangeEvent, FC, useCallback, useContext, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"
import { Image, Options } from "react-native-image-crop-picker"
import { ActivityIndicator } from "react-native-paper"
import ShortUniqueId from "short-unique-id"

import { collections } from "../../common/types"
import { ICON_SIZE, INPUT_HEIGHT } from "../../features/config/Constants"
import currentDateMs from "../../features/hooks/useDateMock"
import { PatientNS, PatientsListNS } from "../../features/i18n/constants"
import { UserData } from "../../features/models/UserData"
import { messageContext } from "../../features/Providers/MessageProvider"
import { getFileName } from "../../utils/getFileName"
import { IChildrenProps } from "./PatientInformationsView"

export const imageOptions: Options = {
  mediaType: "photo",
}

function isImage(source: Fileish): source is Image {
  return (source as Image).path !== undefined
}

function isWeb(source: Fileish): source is File {
  return Platform.OS === "web"
}

const PatientReportView: FC<IChildrenProps> = ({ patient, isSurgeon }) => {
  const {
    dimensions: { spacing },
    colors: {
      primary,
      surface: { textInput },
      black: { mediumEmphasis },
    },
  } = useTheme()
  const { showSnack } = useAlert()
  const { userData } = useContext<IUserContext<UserData>>(userContext)
  const { sendMessage } = useContext(messageContext)
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement & { click: () => void }>(null)

  const s = useMemo(
    () => ({
      row: { padding: spacing },
    }),
    [spacing],
  )

  const saveReport = useCallback(
    async (source: Fileish | Image[] | DocumentPickerResponse[] | undefined) => {
      if (!source) {
        return
      }

      // Si source est un tableau (document picker), prendre le premier élément
      const actualSource = Array.isArray(source) ? source[0] : source

      if (!actualSource) {
        return
      }

      try {
        setLoading(true)
        const uuid = new ShortUniqueId({ length: 28 })
        const imageRef = firebase
          .storage()
          .ref(`${collections.LOGIN}/${patient.id}/${uuid.randomUUID()}`)
        let attachmentName: string
        if (isWeb(actualSource)) {
          await imageRef.put(actualSource)
          attachmentName = actualSource.name
        } else {
          const sourcePath = isImage(actualSource)
            ? actualSource.path
            : Platform.OS === "ios"
            ? actualSource.uri
            : actualSource.fileCopyUri

          if (!sourcePath) {
            throw new Error("Source path is undefined")
          }

          attachmentName = isImage(actualSource)
            ? getFileName(sourcePath, actualSource.filename)
            : actualSource.name

          // Pour Android 13+, si le path commence par content://, on doit utiliser put() avec fetch
          if (Platform.OS === "android" && sourcePath.startsWith("content://")) {
            const response = await fetch(sourcePath)
            const blob = await response.blob()
            await imageRef.put(blob)
          } else {
            await imageRef.putFile(sourcePath)
          }
        }
        const attachmentUrl = await imageRef.getDownloadURL()
        const message = {
          attachmentUrl,
          attachmentName,
          timestamp: firestore.Timestamp.fromMillis(currentDateMs()),
          authorName: t(`${PatientsListNS}:name`, {
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            ns: PatientsListNS,
            interpolation: { escapeValue: false },
          }),
        }
        await sendMessage(patient.id, message)
        showSnack({ message: t(`${PatientNS}:report`) })
      } catch (e) {
        showSnack({ message: t(`${PatientNS}:errors.send_message`) })
        logger("send report error", e)
      } finally {
        setLoading(false)
      }
    },
    [patient.id, sendMessage, setLoading, showSnack, t, userData?.firstName, userData?.lastName],
  )

  const commonIconProps = useMemo<Omit<VectorIconProps, "name">>(
    () => ({
      size: ICON_SIZE,
      color: mediumEmphasis,
      category: "MaterialIcons",
    }),
    [mediumEmphasis],
  )

  const rowReportProps = useMemo(
    () => ({
      color: mediumEmphasis,
      leftChild: <VectorIcon name="file-upload" {...commonIconProps} />,
      rightChild: loading ? <ActivityIndicator color={primary} size="small" /> : null,
      rowStyle: {
        height: INPUT_HEIGHT,
        backgroundColor: textInput,
      },
      onPress: Platform.OS === "web" ? () => fileInputRef.current?.click?.() : undefined,
    }),
    [commonIconProps, loading, mediumEmphasis, primary, textInput],
  )

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file && !isProcessing) {
        setIsProcessing(true)
        try {
          await saveReport(file)
        } finally {
          setIsProcessing(false)
          event.target.value = ""
        }
      }
    },
    [isProcessing, saveReport],
  )

  const rowItem = useMemo(
    () => (
      <RowItem
        testID="add-cr-btn"
        {...rowReportProps}
        name={t(`${PatientNS}:addCr`)}
        color={mediumEmphasis}
        onPress={Platform.OS === "web" ? () => fileInputRef.current?.click() : undefined}
      />
    ),
    [mediumEmphasis, rowReportProps, t],
  )

  return (
    <View style={s.row}>
      {Platform.OS === "web" ? (
        <>
          {rowItem}
          <input
            ref={fileInputRef}
            type="file"
            accept="*/*"
            disabled={isProcessing}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </>
      ) : (
        <PermissionCameraWrapper
          downloadFile
          style={styles.cover}
          disabled={!isSurgeon}
          setSource={saveReport}
          downloadGallery={false}
          imageOptions={imageOptions}
          icons={{
            camera: {
              name: "photo-camera",
              ...commonIconProps,
            },
            documents: {
              name: "file-copy",
              ...commonIconProps,
            },
          }}
        >
          {rowItem}
        </PermissionCameraWrapper>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  cover: {
    height: INPUT_HEIGHT,
    justifyContent: "center",
  },
  clear: {
    justifyContent: "center",
  },
})

export default PatientReportView
